import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuthStore } from '../../store/authStore';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  TextField,
  Typography,
  Box,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  Chip
} from '@mui/material';

const SelectUsersDialog = ({ open, onClose, onSubmit, couponId, couponName, isRevoking }) => {
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  
  const { user: userInfo } = useAuthStore();
  
  useEffect(() => {
    if (open) {
      fetchUsers();
      if (couponId) {
        fetchUsersWithCoupon();
      }
    } else {
      setSelectedUsers([]);
      setUsers([]);
      setError('');
    }
  }, [open, couponId]);
  
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const { data } = await axios.get('/api/users', {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      });
      
      // Фильтруем только активных пользователей
      const activeUsers = data.filter(user => user.isActive);
      setUsers(activeUsers);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };
  
  const [usersWithCoupon, setUsersWithCoupon] = useState([]);
  
  const fetchUsersWithCoupon = async () => {
    try {
      const { data } = await axios.get(`/api/coupons/users-with-coupon/${couponId}`, {
        headers: {
          Authorization: `Bearer ${userInfo.token}`,
        },
      });
      
      if (data.userIds && data.userIds.length > 0) {
        // Сохраняем список пользователей с купоном
        setUsersWithCoupon(data.userIds);
        
        // Убрали автоматическое выделение пользователей с купоном при отзыве
        // Теперь администратор сам выберет, у кого отозвать купон
        
        console.log(`Пользователей с купоном: ${data.count}`);
      }
    } catch (err) {
      console.error('Error fetching users with coupon:', err);
    }
  };
  
  const handleToggleUser = (userId) => {
    const currentIndex = selectedUsers.indexOf(userId);
    const newSelectedUsers = [...selectedUsers];
    
    if (currentIndex === -1) {
      newSelectedUsers.push(userId);
    } else {
      newSelectedUsers.splice(currentIndex, 1);
    }
    
    setSelectedUsers(newSelectedUsers);
    
    // Update selectAll state based on if all users are selected
    setSelectAll(newSelectedUsers.length === users.length);
  };
  
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedUsers([]);
    } else {
      const allUserIds = users.map(user => user.id);
      setSelectedUsers(allUserIds);
    }
    setSelectAll(!selectAll);
  };
  
  const handleSubmit = () => {
    if (selectedUsers.length === 0) {
      setError('Выберите хотя бы одного пользователя');
      return;
    }
    
    onSubmit(selectedUsers);
  };
  
  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(searchLower) ||
      user.email?.toLowerCase().includes(searchLower) ||
      user.phone?.toLowerCase().includes(searchLower)
    );
  });
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isRevoking 
          ? `Выберите пользователей для отзыва купона: ${couponName}` 
          : `Выберите пользователей для распределения купона: ${couponName}`
        }
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center" sx={{ my: 2 }}>
            {error}
          </Typography>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Поиск пользователей"
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Поиск по имени, email или телефону"
                margin="normal"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={selectAll}
                    onChange={handleSelectAll}
                    color="primary"
                  />
                }
                label="Выбрать всех"
              />
            </Box>
            
            <Divider sx={{ my: 1 }} />
            
            <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
              Выбрано пользователей: {selectedUsers.length} из {users.length}
            </Typography>
            
            <List sx={{ maxHeight: 400, overflow: 'auto' }}>
              {filteredUsers.map((user) => (
                <ListItem 
                  key={user.id} 
                  button 
                  onClick={() => handleToggleUser(user.id)}
                  sx={{ 
                    borderBottom: '1px solid #eee',
                    bgcolor: selectedUsers.includes(user.id) 
                      ? 'rgba(0, 0, 0, 0.04)' 
                      : usersWithCoupon.includes(user.id)
                        ? (isRevoking ? 'rgba(255, 152, 0, 0.1)' : 'rgba(76, 175, 80, 0.1)')
                        : 'transparent'
                  }}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={selectedUsers.includes(user.id)}
                      tabIndex={-1}
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText 
                    primary={
                      <>
                        {user.name || 'Без имени'}
                        {usersWithCoupon.includes(user.id) && (
                          <Chip 
                            size="small" 
                            label="Есть купон" 
                            color={isRevoking ? "warning" : "success"}
                            variant="outlined"
                            sx={{ ml: 1, fontSize: '0.7rem' }}
                          />
                        )}
                      </>
                    } 
                    secondary={
                      <React.Fragment>
                        <Typography component="span" variant="body2" color="textPrimary">
                          {user.email}
                        </Typography>
                        {user.phone && ` • ${user.phone}`}
                        {` • ${user.role === 'admin' ? 'Администратор' : 'Клиент'}`}
                      </React.Fragment>
                    }
                  />
                </ListItem>
              ))}
              {filteredUsers.length === 0 && (
                <Typography align="center" sx={{ py: 2 }}>
                  Пользователи не найдены
                </Typography>
              )}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Отмена
        </Button>
        <Button 
          onClick={handleSubmit} 
          color={isRevoking ? "error" : "primary"} 
          variant="contained" 
          disabled={selectedUsers.length === 0}
        >
          {isRevoking ? 'Отозвать купон' : 'Распределить купон'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SelectUsersDialog;
